import { useState } from 'react';

/**
 * オプションごとの色指定（横断 PMO のチーム filter で使用）。
 *
 * 戻り値が定義された場合、ドロップダウンの各項目を `bg` 背景・`text` 文字色のラベル
 * チップとして描画する（横断 PMO は淡色 + Div 系統色で視認性を上げる）。
 */
interface OptionColor {
  readonly bg: string;
  readonly text: string;
}

/**
 * フィルタ付きヘッダーセル
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business テーブルヘッダーにセレクトボックスフィルタを統合。ラベル+ドロップダウンで列フィルタリングを提供
 * @graph-connects none
 */
export function FilterableHeader({
  label,
  value,
  options,
  onChange,
  getOptionColor,
  locked = false,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  /** 任意。オプション値ごとの色を返す関数。未指定なら通常のテキスト表示。 */
  getOptionColor?: (opt: string) => OptionColor | undefined;
  /**
   * true の場合、ドロップダウン開閉と onChange を無効化し、固定表示にする。
   * team_leader 等、値を変更させたくないユーザー向け。
   */
  locked?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selectedColor = value && getOptionColor ? getOptionColor(value) : undefined;
  return (
    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap relative">
      <button
        type="button"
        disabled={locked}
        className={`inline-flex items-center gap-1 transition-colors ${
          locked ? 'cursor-default' : 'hover:text-[var(--color-text-primary)]'
        } ${value ? 'text-[var(--color-accent-primary)]' : ''}`}
        onClick={() => {
          if (locked) return;
          setOpen(!open);
        }}
      >
        {/* 色付きチップ指定の選択中: ラベルを消して team 名 (chip) のみをタイトル化 */}
        {selectedColor ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
            style={{ backgroundColor: selectedColor.bg, color: selectedColor.text }}
          >
            {value}
          </span>
        ) : (
          label
        )}
        {/* locked 時は ▼ アイコンを非表示にし、固定であることを視覚的に示す */}
        {!locked && (
          <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
            <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        )}
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setOpen(false);
            }}
          />
          <div className="absolute top-full left-2 z-20 mt-1 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-lg shadow-lg py-1 min-w-[140px] max-h-[240px] overflow-y-auto flex flex-col">
            <button
              type="button"
              className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--color-bg-hover)] ${!value ? 'text-[var(--color-accent-primary)] font-medium' : 'text-[var(--color-text-primary)]'}`}
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              すべて
            </button>
            {options.map((opt) => {
              const color = getOptionColor?.(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--color-bg-hover)] ${value === opt ? 'text-[var(--color-accent-primary)] font-medium' : 'text-[var(--color-text-primary)]'}`}
                  onClick={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                >
                  {color ? (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                      style={{ backgroundColor: color.bg, color: color.text }}
                    >
                      {opt}
                    </span>
                  ) : (
                    opt
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </th>
  );
}

/**
 * 進捗率フィルタヘッダー
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business テーブルヘッダーに進捗率範囲フィルタ（0-50%/50-80%/80%+）のドロップダウンを統合
 * @graph-connects none
 */
export function ProgressFilterHeader({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const opts = [
    { key: '', label: 'すべて' },
    { key: 'low', label: '0〜40%', color: '#38bdf8' },
    { key: 'mid', label: '40〜90%', color: '#facc15' },
    { key: 'high', label: '90〜100%', color: '#22c55e' },
  ];
  return (
    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap min-w-[160px] relative">
      <button
        type="button"
        className={`inline-flex items-center gap-1 hover:text-[var(--color-text-primary)] transition-colors ${value ? 'text-[var(--color-accent-primary)]' : ''}`}
        onClick={() => {
          setOpen(!open);
        }}
      >
        {value && (
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: opts.find((o) => o.key === value)?.color }}
          />
        )}
        {label}
        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
          <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setOpen(false);
            }}
          />
          <div className="absolute top-full left-2 z-20 mt-1 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-lg shadow-lg py-1 min-w-[120px]">
            {opts.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--color-bg-hover)] flex items-center gap-2 ${value === opt.key ? 'text-[var(--color-accent-primary)] font-medium' : 'text-[var(--color-text-primary)]'}`}
                onClick={() => {
                  onChange(opt.key);
                  setOpen(false);
                }}
              >
                {opt.color && (
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: opt.color }} />
                )}
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </th>
  );
}
